<template>
    <div style="width: 100%">
        <common-title-panel :title="title" :subTitle="subTitle"></common-title-panel>
        <div ref="chart" :style="{ width: '100%', height: chartHeight + 'vh' }"></div>
    </div>
</template>

<script>
/**
 * data, title 등을 props 로 받아 전월, 당월, 전년 동월을 표현하는 부분에 공통 stack 차트로 사용
 * TODO: 현재 title 은 props 로 받고 있고 data, option 등을 propos 로 받는 작업 해야함.
 */
import * as echarts from "echarts";
import CommonTitlePanel from "../panels/CommonTitlePanel.vue";
export default {
    props: {
        title: {
            type: String,
            default: "",
        },
        subTitle: {
            type: String,
            default: "",
        },
        chartHeight: {
            type: Number,
            default: 300,
        },
        data: {
            type: Object,
            default: () => {},
        },
        color: {
            type: Object,
            default: () => {},
        },
        xAxisLabel: {
            type: Array,
            default: () => [],
        },
        showLabel: {
            type: Boolean,
            default: true,
        },
        usageAnalysis: {
            type: Object,
            default: () => {},
        },
    },
    components: { CommonTitlePanel },
    data() {
        return {
            chart: null,
            baseColor: "#69696966",
            resizeHandler: null, // resize 핸들러 참조 저장
        };
    },
    watch: {
        data: {
            async handler(newVal) {
                this.chartData = newVal;
                await this.initChart();
            },
        },
    },
    async mounted() {
        this.chartData = this.data;
        await this.initChart();

        // resize 핸들러를 메서드로 저장
        this.resizeHandler = this.resizeChart.bind(this);
        this.$nextTick(() => {
            window.addEventListener("resize", this.resizeHandler);
        });
    },

    beforeDestroy() {
        this.cleanupChart();
    },

    destroyed() {
        // console.log(`[DESTROYED] BarChart`);
    },
    methods: {
        /* 차트 정리 메서드 */
        cleanupChart() {
            // 이벤트 리스너 제거
            if (this.resizeHandler) {
                window.removeEventListener("resize", this.resizeHandler);
                this.resizeHandler = null;
            }

            // 차트 인스턴스 정리
            if (this.chart) {
                this.chart.dispose();
                this.chart = null;
            }

            // 데이터 참조 해제
            this.chartData = null;
        },

        initChart() {
            // 기존 차트가 있으면 정리
            if (this.chart) {
                this.chart.dispose();
            }

            this.chart = echarts.init(this.$refs.chart);
            const chartData = this.chartData;
            const q1 = this.usageAnalysis?.stats?.quartile?.Q1;
            const q3 = this.usageAnalysis?.stats?.quartile?.Q3;
            const title = this.title;
            const option = {
                tooltip: {
                    trigger: "axis",
                    axisPointer: { type: "shadow" },
                    formatter: (params) => {
                        const axisLabel = params[0]?.axisValueLabel || params[0]?.axisValue || "";
                        const dataIndex = params[0]?.dataIndex;

                        const debugInfo = Object.keys(chartData)
                            .map((key) => `${chartData[key][dataIndex]}`)
                            .join("<br/>");

                        return `
            <div style=" margin-bottom:4px; width: 200px">${axisLabel}</div>
            <div style="display: flex; justify-content: space-between;">

                <div>
                        <span style="display:inline-block;margin-right:6px;border-radius:50%;width:10px;height:10px;background:#69696966"></span>소비량  
                    </div>
              <strong>${debugInfo.toLocaleString()}</strong>
            </div>
        
        `;
                    },
                },

                legend: {
                    bottom: 0,
                    itemWidth: 15,
                    itemHeight: 10,
                    textStyle: {
                        fontSize: 12,
                    },
                },
                grid: {
                    top: 10,
                    left: "3%",
                    right: !this.isEmpty(this.usageAnalysis) ? "6%" : "3%",
                    bottom: 50,
                },
                xAxis: {
                    type: "category",
                    data: this.xAxisLabel,
                },
                yAxis: {
                    type: "value",
                    axisLine: { show: false },
                    axisLabel: { show: false },
                    axisTick: { show: false },
                    splitLine: {
                        show: true,
                        lineStyle: {
                            color: "#e0e0e0",
                            type: !this.isEmpty(this.usageAnalysis) ? "solid" : "dashed",
                        },
                    },
                },
            };

            // const q1 = this.usageAnalysis?.stats?.quartile?.Q1;
            // const q3 = this.usageAnalysis?.stats?.quartile?.Q3;

            const markAreaData = [];

            if (!this.isEmpty(this.chartData)) {
                const firstKey = Object.keys(this.chartData)[0];
                const seriesData = this.chartData[firstKey];

                option.series = [];
                option.legend.data = Object.keys(this.chartData);

                this.xAxisLabel.forEach((label, idx) => {
                    const value = seriesData[idx];
                    const prevValue = seriesData[idx - 1];
                    const nextValue = seriesData[idx + 1];
                    const nextLabel = this.xAxisLabel[idx + 1];

                    if (typeof value !== "number") return;

                    // Q1 이하
                    if (typeof q1 === "number" && value < q1) {
                        const prevIsLow = typeof prevValue === "number" && prevValue < q1;
                        const nextIsLow = typeof nextValue === "number" && nextValue < q1;

                        const isIsolated = !prevIsLow && !nextIsLow;

                        if (isIsolated && nextLabel) {
                            markAreaData.push([
                                {
                                    xAxis: label,
                                    itemStyle: { color: "rgba(0, 100, 255, 0.1)" },
                                },
                                { xAxis: nextLabel },
                            ]);
                        } else {
                            markAreaData.push([
                                {
                                    xAxis: label,
                                    itemStyle: { color: "rgba(0, 100, 255, 0.1)" },
                                },
                                { xAxis: label },
                            ]);
                        }
                    }

                    // Q3 이상
                    if (typeof q3 === "number" && value > q3) {
                        const prevIsHigh = typeof prevValue === "number" && prevValue > q3;
                        const nextIsHigh = typeof nextValue === "number" && nextValue > q3;

                        const isIsolated = !prevIsHigh && !nextIsHigh;

                        if (isIsolated && nextLabel) {
                            markAreaData.push([
                                {
                                    xAxis: label,
                                    itemStyle: { color: "rgba(255, 0, 0, 0.1)" },
                                },
                                { xAxis: nextLabel },
                            ]);
                        } else {
                            markAreaData.push([
                                {
                                    xAxis: label,
                                    itemStyle: { color: "rgba(255, 0, 0, 0.1)" },
                                },
                                { xAxis: label },
                            ]);
                        }
                    }
                });
                Object.keys(this.chartData).forEach((key, index) => {
                    const fullData = this.chartData[key];

                    const baseData = fullData.map((val) => {
                        if (val == null) return null;
                        if (typeof q1 === "number" && val < q1) return null;
                        if (typeof q3 === "number" && val > q3) return q3;
                        return val;
                    });

                    const highlightData = fullData.map((val) => {
                        if (val == null) return null;

                        // Q1보다 작을 경우 전체를 highlight에 넣기
                        if (typeof q1 === "number" && val < q1) return val;

                        // Q3보다 클 경우 초과분만 highlight에
                        if (typeof q3 === "number" && val > q3) return val - q3;

                        return null;
                    });

                    const baseSeries = {
                        name: key,
                        type: "bar",
                        stack: key,
                        data: baseData.map((val, idx) => {
                            if (val === 0 || val == null) return null;
                            return {
                                value: val,
                                itemStyle: {
                                    borderRadius:
                                        typeof q1 === "number" &&
                                        typeof q3 === "number" &&
                                        fullData[idx] > q1 &&
                                        fullData[idx] < q3
                                            ? [10, 10, 0, 0]
                                            : 0,
                                },
                            };
                        }),
                        itemStyle: {
                            color: this.baseColor,
                        },
                        label: {
                            show: this.showLabel,
                            position: "inside",
                            fontSize: 12,
                            color: "#303030",
                            textBorderColor: "#fff",
                            textBorderWidth: 1,
                            formatter: (params) => {
                                if (title.includes("비용")) {
                                    const divided = Math.floor(params.data.value / 1000);
                                    return divided >= 1000 ? divided.toLocaleString() : divided;
                                } else {
                                    return params.data.value >= 1000
                                        ? params.data.value.toLocaleString()
                                        : params.data.value;
                                }
                            },
                        },
                    };

                    const highlightSeries = {
                        name: key + "_highlight",
                        type: "bar",
                        stack: key,
                        data: highlightData.map((item) => (item === 0 ? null : item)),
                        itemStyle: {
                            color: (params) => {
                                const originalVal = fullData[params.dataIndex];
                                if (typeof q1 === "number" && originalVal < q1) {
                                    return "rgba(0, 100, 255, 0.6)";
                                }
                                if (typeof q3 === "number" && originalVal > q3) {
                                    return "rgba(255, 0, 0, 0.6)";
                                }
                                return "rgba(0,0,0,0)";
                            },
                            borderRadius: [10, 10, 0, 0],
                        },
                        label: { show: false },
                        tooltip: { show: false },
                    };

                    if (index === 0) {
                        baseSeries.markLine = {
                            symbol: "none",
                            label: {
                                show: true,
                                formatter: (param) => param.name,
                                position: "end",
                                fontSize: 12,
                                color: "#212121",
                            },
                            lineStyle: {
                                type: "dashed",
                                color: "#212121",
                                width: 1,
                            },
                            data: [
                                ...(typeof q1 === "number" ? [{ yAxis: q1, name: "Q1" }] : []),
                                ...(typeof q3 === "number" ? [{ yAxis: q3, name: "Q3" }] : []),
                            ],
                        };
                        baseSeries.markArea = {
                            silent: true,
                            data: markAreaData,
                        };
                    }

                    option.series.push(baseSeries, highlightSeries);
                });
            }
            this.chart.setOption(option);
        },
        resizeChart() {
            if (this.chart && !this.chart.isDisposed()) {
                this.chart.resize();
            }
        },
    },
};
</script>

<style scoped>
.chart-container {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: space-between;
    flex-direction: column;
}
</style>
